// Copyright 2025, University of Maryland, All Rights Reserved
import { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchUserProfile, fetchOrganizations, fetchOrgWorkspaces } from '../services';
import { WorkspaceInfo, OrganizationResponse } from '../types/workspace';
import {
  getActiveWorkspace,
  saveActiveWorkspace,
  clearActiveWorkspace,
  getUrlWorkspace,
} from '../lib/workspace-utils';
import { USER_PROFILE_QUERY_KEY } from '../hooks/use-user-profile';
import { ORGANIZATIONS_QUERY_KEY } from '../hooks/use-organizations';
import { workspacesQueryKey } from '../hooks/use-workspaces';
import { setImpersonationTarget } from '../services/impersonation-session';
import { canAdministerWorkspace } from '../lib/workspace-access';

interface User {
  // CAS fields
  username?: string;
  email?: string;
  name?: string;
  token?: string;
  umid?: string;
  exp?: number;
  iat?: number;
  isGlobalAdmin?: boolean;
  attributes?: Record<string, unknown>;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  userId: string | null;
  isGlobalAdmin: boolean;
  // Presentation only — the backend re-derives org administration on every
  // request; no mutation may trust this flag.
  isOrgAdmin: boolean;
  isWorkspaceAdmin: boolean;
  selectedOrganizationId: string | null;
  selectedWorkspaceId: string | null;
  accessibleOrganizations: OrganizationResponse[];
  accessibleWorkspaces: WorkspaceInfo[];
  currentWorkspace: WorkspaceInfo | null;
  currentOrgRoles: string[];
  canCreateWorkspace: boolean;
  creatableAppIds: string[];
  selectWorkspace: (orgId: string, wsId: string) => void;
  selectOrganization: (orgId: string) => Promise<void>;
  loadingWorkspaces: boolean;
  workspacesError: string | null;
  hasWorkspaceAccess: boolean;
  impersonatingUserId: string | null;
  actualUserId: string | null;
  logout?: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export for testing - allows tests to provide mock context values
export { AuthContext };

interface AuthProviderProps {
  children: ReactNode;
  appName: string;
  logout?: () => void;
}

export function AuthProvider({ children, appName, logout }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const authRecoveryStartedRef = useRef(false);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [accessibleOrganizations, setAccessibleOrganizations] = useState<OrganizationResponse[]>([]);
  const [accessibleWorkspaces, setAccessibleWorkspaces] = useState<WorkspaceInfo[]>([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [workspacesError, setWorkspacesError] = useState<string | null>(null);
  const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);
  const [actualUserId, setActualUserId] = useState<string | null>(null);

  const recoverInvalidSession = useCallback(() => {
    if (authRecoveryStartedRef.current) {
      return;
    }
    authRecoveryStartedRef.current = true;

    console.warn(`[AuthContext-${appName}] Invalid auth session detected, resetting session`);
    setIsAuthenticated(false);
    setUser(null);
    setUserId(null);
    setAccessibleOrganizations([]);
    setAccessibleWorkspaces([]);
    setSelectedOrganizationId(null);
    setSelectedWorkspaceId(null);
    setIsGlobalAdmin(false);
    setImpersonatingUserId(null);
    setActualUserId(null);
    setWorkspacesError('Your session is invalid. Restarting login...');
    setImpersonationTarget(null);

    if (logout) {
      logout();
      return;
    }

    localStorage.removeItem('cas_jwt');
    localStorage.removeItem('cas_user');
    localStorage.removeItem('cas_redirect_path');
    localStorage.removeItem('cas_redirect_count');
    window.location.reload();
  }, [appName, logout]);

  // Shared authentication handler
  const handleAuthentication = useCallback(async (authenticated: boolean, user: User | null) => {
    console.log(`[AuthContext-${appName}] Handling authentication:`, authenticated);

    setIsAuthenticated(authenticated);
    setUser(user);

    if (authenticated && user) {
      let extractedUserId = null;

      // Extract user ID from CAS authentication
      if (user.username) {
        extractedUserId = user.username;
      } else if (user.email) {
        extractedUserId = user.email;
      }
      console.log(`[AuthContext-${appName}] Extracting userId from CAS:`, extractedUserId);

      if (extractedUserId) {
        console.log(`[AuthContext-${appName}] User ID set:`, extractedUserId);
        setUserId(extractedUserId);

        try {
          console.log(`[AuthContext-${appName}] Fetching user data...`);
          setLoadingWorkspaces(true);

          // Fetch user profile first using React Query cache
          const userProfile = await queryClient.fetchQuery({
            queryKey: USER_PROFILE_QUERY_KEY,
            queryFn: fetchUserProfile,
            staleTime: 5 * 60 * 1000, // 5 minutes
          });
          console.log(`[AuthContext-${appName}] User profile loaded:`, userProfile);

          // Security: application features act as the effective profile user.
          // actualUserId separately retains the administrator for audit UI.
          setUserId(userProfile.userId);

          // Handle impersonation - check if this user is being impersonated by an admin
          if (userProfile.impersonatedBy) {
            // We are viewing as an impersonated user
            // userProfile is the effective user (the one being impersonated)
            // userProfile.impersonatedBy is the admin doing the impersonation
            console.log(`[AuthContext-${appName}] Being impersonated by admin:`, userProfile.impersonatedBy.userId);
            setImpersonatingUserId(userProfile.userId);  // The user being impersonated
            setActualUserId(userProfile.impersonatedBy.userId);  // The real admin doing the impersonation
            // Use the effective (impersonated) user's admin status for permissions
            setIsGlobalAdmin(userProfile.isGlobalAdmin ?? false);
          } else {
            // No impersonation - normal login
            setImpersonatingUserId(null);
            setActualUserId(null);
            setIsGlobalAdmin(userProfile.isGlobalAdmin ?? false);
          }

          // Fetch organizations using React Query cache
          const organizations = await queryClient.fetchQuery({
            queryKey: ORGANIZATIONS_QUERY_KEY,
            queryFn: fetchOrganizations,
            staleTime: 5 * 60 * 1000, // 5 minutes
          });
          console.log(`[AuthContext-${appName}] Organizations loaded:`, organizations.length);
          setAccessibleOrganizations(organizations);

          // Auto-select organization logic
          // Prefer non-system orgs (those not starting with '_')
          let autoSelectedOrgId: string | null = null;
          const nonSystemOrgs = organizations.filter(org => !org.orgId.startsWith('_'));
          const orgsToSelect = nonSystemOrgs.length > 0 ? nonSystemOrgs : organizations;

          if (orgsToSelect.length === 1) {
            console.log(`[AuthContext-${appName}] Auto-selecting single available org:`, orgsToSelect[0].orgId);
            autoSelectedOrgId = orgsToSelect[0].orgId;
          } else if (orgsToSelect.length > 1) {
            console.log(`[AuthContext-${appName}] Auto-selecting first org from ${orgsToSelect.length} available:`, orgsToSelect[0].orgId);
            autoSelectedOrgId = orgsToSelect[0].orgId;
          }

          // If the URL already specifies org+ws, defer to it and skip the saved
          // workspace — otherwise the saved value can disagree with the URL and
          // cause the deep-link/URL-sync effects in Layout to ping-pong.
          // cm-coding: DocReview uses HashRouter, so its workspace query lives
          // after '#'. Reading window.location.search makes authentication pick
          // a default while Layout applies the hash workspace, and the two
          // selections can alternate indefinitely after impersonation changes.
          const urlWorkspace = getUrlWorkspace();
          const urlOrg = urlWorkspace?.orgId ?? null;
          const urlWs = urlWorkspace?.wsId ?? null;
          const effectiveUserId = userProfile.userId;
          const savedWorkspace = urlWorkspace
            ? null
            : getActiveWorkspace(effectiveUserId);
          let orgToLoad = urlOrg ?? autoSelectedOrgId;

          if (urlOrg && urlWs) {
            console.log(`[AuthContext-${appName}] URL has org+ws, deferring to URL:`, { urlOrg, urlWs });
          } else if (savedWorkspace?.orgId && savedWorkspace?.wsId) {
            // Only use saved workspace if it has valid org and ws IDs
            orgToLoad = savedWorkspace.orgId;
            console.log(`[AuthContext-${appName}] Found saved workspace, will load org:`, orgToLoad);
          } else if (savedWorkspace) {
            // Clear corrupted saved workspace
            console.log(`[AuthContext-${appName}] Clearing invalid saved workspace (missing orgId or wsId)`);
            clearActiveWorkspace(effectiveUserId);
          }

          // Load workspaces for the selected organization using React Query cache
          if (orgToLoad) {
            console.log(`[AuthContext-${appName}] Loading workspaces for org:`, orgToLoad);
            const workspaces = await queryClient.fetchQuery({
              queryKey: workspacesQueryKey(orgToLoad),
              queryFn: () => fetchOrgWorkspaces(orgToLoad),
              staleTime: 5 * 60 * 1000, // 5 minutes
            });
            console.log(`[AuthContext-${appName}] Workspaces loaded for org ${orgToLoad}:`, workspaces.length);
            setAccessibleWorkspaces(workspaces);
            setSelectedOrganizationId(orgToLoad);

            // Handle workspace selection
            if (urlOrg && urlWs && urlOrg === orgToLoad && workspaces.some(ws => ws.wsId === urlWs)) {
              console.log(`[AuthContext-${appName}] Using URL workspace:`, urlWs);
              setSelectedWorkspaceId(urlWs);
              saveActiveWorkspace(effectiveUserId, orgToLoad, urlWs);
            } else if (savedWorkspace && savedWorkspace.orgId === orgToLoad) {
              // Validate saved workspace still exists
              const workspaceExists = workspaces.some(ws => ws.wsId === savedWorkspace.wsId);
              if (workspaceExists) {
                console.log(`[AuthContext-${appName}] Using saved workspace:`, savedWorkspace.wsId);
                setSelectedWorkspaceId(savedWorkspace.wsId);
              } else {
                console.log(`[AuthContext-${appName}] Saved workspace no longer exists, clearing`);
                clearActiveWorkspace(effectiveUserId);
                // Auto-select first workspace if available
                if (workspaces.length > 0) {
                  setSelectedWorkspaceId(workspaces[0].wsId);
                  saveActiveWorkspace(effectiveUserId, orgToLoad, workspaces[0].wsId);
                }
              }
            } else if (workspaces.length === 1) {
              // Auto-select if only one workspace
              console.log(`[AuthContext-${appName}] Auto-selecting single workspace:`, workspaces[0].wsId);
              setSelectedWorkspaceId(workspaces[0].wsId);
              saveActiveWorkspace(effectiveUserId, orgToLoad, workspaces[0].wsId);
            } else if (workspaces.length > 1) {
              // Select first workspace as default
              console.log(`[AuthContext-${appName}] Auto-selecting first workspace:`, workspaces[0].wsId);
              setSelectedWorkspaceId(workspaces[0].wsId);
              saveActiveWorkspace(effectiveUserId, orgToLoad, workspaces[0].wsId);
            }

            if (workspaces.length === 0 && !userProfile.isGlobalAdmin) {
              console.log(`[AuthContext-${appName}] No accessible workspaces in org - access will be denied`);
            }
          } else if (organizations.length === 0 && !userProfile.isGlobalAdmin) {
            console.log(`[AuthContext-${appName}] No accessible organizations - access will be denied`);
          }

          setWorkspacesError(null);
        } catch (error) {
          console.error(`[AuthContext-${appName}] Error fetching user data:`, error);

          const status = typeof error === 'object' && error !== null && 'response' in error
            ? (error as { response?: { status?: number } }).response?.status
            : undefined;

          if (status === 401 || status === 403) {
            recoverInvalidSession();
            return;
          }

          setWorkspacesError('Failed to load user data');
        } finally {
          setLoadingWorkspaces(false);
        }
      } else {
        console.error(`[AuthContext-${appName}] Could not extract userId from user object`);
        setWorkspacesError('Failed to extract user ID from authentication data');
      }
    }

    setIsLoading(false);
  }, [appName, queryClient, recoverInvalidSession]);

  // Check for existing CAS session on mount
  useEffect(() => {
    console.log(`[AuthContext-${appName}] Checking for existing CAS session on mount`);
    try {
      const casUser = localStorage.getItem('cas_user');
      const casToken = localStorage.getItem('cas_jwt');

      if (casUser && casToken) {
        console.log(`[AuthContext-${appName}] Found existing CAS session, triggering auth flow`);
        const user = JSON.parse(casUser);
        handleAuthentication(true, user);
        return; // Don't set isLoading to false yet, handleAuthentication will do it
      } else {
        console.log(`[AuthContext-${appName}] No existing CAS session found on mount`);
        setIsLoading(false);
      }
    } catch (error) {
      console.error(`[AuthContext-${appName}] Error checking CAS session:`, error);
      setIsLoading(false);
    }
  }, [appName, handleAuthentication]);

  useLayoutEffect(() => {
    console.log(`[AuthContext-${appName}] Setting up event listeners`);

    const handleAuthStateChange = async (event: Event) => {
      console.log(`[AuthContext-${appName}] Event received:`, event.type);
      const customEvent = event as CustomEvent;
      const { authenticated, user } = customEvent.detail;

      // Use the shared authentication handler
      await handleAuthentication(authenticated, user);
    };

    console.log(`[AuthContext-${appName}] Adding event listeners for auth-state-change and access-granted (CAS auth)`);
    document.addEventListener('auth-state-change', handleAuthStateChange);
    document.addEventListener('access-granted', handleAuthStateChange);

    return () => {
      document.removeEventListener('auth-state-change', handleAuthStateChange);
      document.removeEventListener('access-granted', handleAuthStateChange);
    };
  }, [appName, handleAuthentication]);

  const selectWorkspace = (orgId: string, wsId: string) => {
    console.log(`[AuthContext-${appName}] Selecting workspace:`, { orgId, wsId });

    setSelectedOrganizationId(orgId);
    setSelectedWorkspaceId(wsId);

    if (userId) {
      saveActiveWorkspace(userId, orgId, wsId);
    }
  };

  const selectOrganization = async (orgId: string) => {
    console.log(`[AuthContext-${appName}] Selecting organization:`, orgId);

    if (orgId !== selectedOrganizationId) {
      setSelectedOrganizationId(orgId);
      setSelectedWorkspaceId(null);
      setLoadingWorkspaces(true);

      // Clear old workspace selection
      if (selectedOrganizationId) {
        localStorage.removeItem(`activeWorkspace_${selectedOrganizationId}`);
      }

      try {
        // Fetch workspaces for the new organization using React Query cache
        console.log(`[AuthContext-${appName}] Loading workspaces for org:`, orgId);
        const workspaces = await queryClient.fetchQuery({
          queryKey: workspacesQueryKey(orgId),
          queryFn: () => fetchOrgWorkspaces(orgId),
          staleTime: 5 * 60 * 1000, // 5 minutes
        });
        console.log(`[AuthContext-${appName}] Workspaces loaded for org ${orgId}:`, workspaces.length);
        setAccessibleWorkspaces(workspaces);

        if (workspaces.length === 1) {
          // Auto-select if only one workspace in this org
          console.log(`[AuthContext-${appName}] Auto-selecting single workspace in org:`, workspaces[0]);
          setSelectedWorkspaceId(workspaces[0].wsId);
          if (userId) {
            saveActiveWorkspace(userId, orgId, workspaces[0].wsId);
          }
        } else if (workspaces.length > 1) {
          // Multiple workspaces - select first one as default
          console.log(`[AuthContext-${appName}] Auto-selecting first workspace in org:`, workspaces[0]);
          setSelectedWorkspaceId(workspaces[0].wsId);
          if (userId) {
            saveActiveWorkspace(userId, orgId, workspaces[0].wsId);
          }
        } else {
          // No workspaces in this org
          console.log(`[AuthContext-${appName}] No workspaces available in org ${orgId}`);
        }
      } catch (error) {
        console.error(`[AuthContext-${appName}] Error loading workspaces for org ${orgId}:`, error);
        setWorkspacesError(`Failed to load workspaces for organization`);
        setAccessibleWorkspaces([]);
      } finally {
        setLoadingWorkspaces(false);
      }
    }
  };

  const hasWorkspaceAccess = Boolean(selectedWorkspaceId) || isGlobalAdmin;

  const currentWorkspace = accessibleWorkspaces.find(
    ws => ws.orgId === selectedOrganizationId && ws.wsId === selectedWorkspaceId
  ) || null;

  // Derive org roles and workspace creation permissions from selected org
  const currentOrg = accessibleOrganizations.find(o => o.orgId === selectedOrganizationId);
  const currentOrgRoles = currentOrg?.roles ?? [];
  const isOrgAdmin = currentOrgRoles.includes('admin');
  const isWorkspaceAdmin = canAdministerWorkspace(
    currentOrgRoles,
    currentWorkspace?.role,
  );
  const creatableAppIds = currentOrgRoles
    .filter(r => r.startsWith('workspace_creator_'))
    .map(r => r.replace('workspace_creator_', ''));
  const canCreateWorkspace = isGlobalAdmin || isOrgAdmin || creatableAppIds.length > 0;

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    userId,
    isGlobalAdmin,
    isOrgAdmin,
    isWorkspaceAdmin,
    currentOrgRoles,
    canCreateWorkspace,
    creatableAppIds,
    selectedOrganizationId,
    selectedWorkspaceId,
    accessibleOrganizations,
    accessibleWorkspaces,
    currentWorkspace,
    selectWorkspace,
    selectOrganization,
    loadingWorkspaces,
    workspacesError,
    hasWorkspaceAccess,
    impersonatingUserId,
    actualUserId,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
